import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PhotoLibraryPicker from "@/components/PhotoLibraryPicker";
import ShareButton from "@/components/ShareButton";
import { Plus, X, Camera, ArrowsHorizontal, MapPin, Clock } from "@phosphor-icons/react";

/**
 * ThenAndNow — /observations/then-and-now
 * A cross-site historical vs current photo archive. Anyone signed in
 * (including alumni signed in via partner JWT from alumnipulse.net or
 * indiaalumni.com) can submit a pair of photos of the same place.
 */
const CATEGORIES = [
  { key: "",         label: "All" },
  { key: "nature",   label: "Nature & landscape" },
  { key: "campus",   label: "Alumni · campus" },
  { key: "city",     label: "City & streets" },
  { key: "coast",    label: "Coast & beaches" },
  { key: "farm",     label: "Farms & fields" },
  { key: "wildlife", label: "Wildlife" },
  { key: "forest",   label: "Forests" },
  { key: "water",    label: "Rivers & lakes" },
  { key: "other",    label: "Other" },
];

export default function ThenAndNow() {
  const { user } = useAuth();
  const [pairs, setPairs] = useState([]);
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (source) params.set("source", source);
      params.set("limit", "60");
      const { data } = await http.get(`/then-and-now?${params}`);
      setPairs(data.pairs || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load Then & Now archive");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, source]);

  return (
    <main className="min-h-screen pt-28 md:pt-32 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="then-and-now-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              NaturePulse · Living Archive
            </div>
            <h1 className="mt-2 font-serif-display text-5xl md:text-6xl leading-[0.95] tracking-tighter">
              Then &amp; Now.
            </h1>
            <p className="mt-4 text-[#3A3830] leading-relaxed">
              A living visual archive of how our world has changed. Alumni from{" "}
              <a href="https://alumnipulse.net" className="underline text-[color:var(--np-brand,#B85F2A)]" target="_blank" rel="noreferrer">alumnipulse.net</a>{" "}
              and{" "}
              <a href="https://indiaalumni.com" className="underline text-[color:var(--np-brand,#B85F2A)]" target="_blank" rel="noreferrer">indiaalumni.com</a>{" "}
              contribute campus &amp; hometown pairs; NaturePulse contributors add nature, coasts, wildlife
              and farms. Slide the divider on any pair to reveal the transformation.
            </p>
          </div>
          <button
            data-testid="then-now-submit-btn"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
          >
            <Plus size={13} weight="bold" /> Submit a pair
          </button>
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="then-now-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.key || "all"}
              data-testid={`then-now-cat-${c.key || "all"}`}
              onClick={() => setCategory(c.key)}
              className={
                "rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase " +
                (category === c.key
                  ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
                  : "bg-white border border-black/15 hover:bg-black/5")
              }
            >{c.label}</button>
          ))}
          <select
            data-testid="then-now-source"
            value={source} onChange={(e) => setSource(e.target.value)}
            className="rounded-full border border-black/15 bg-white px-3 py-1.5 text-sm"
          >
            <option value="">All sources</option>
            <option value="naturepulse">NaturePulse</option>
            <option value="alumnipulse">AlumniPulse</option>
            <option value="indiaalumni">IndiaAlumni</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="mt-4 text-[10px] font-mono-tech tracking-[0.28em] uppercase text-[#8E8B7F]">
          {loading ? "Loading…" : `${total} approved pair${total === 1 ? "" : "s"}`}
        </div>

        {/* Grid */}
        {!loading && pairs.length === 0 && (
          <div className="mt-8 rounded-2xl border border-dashed border-black/15 bg-white/60 p-16 text-center">
            <p className="font-serif-display text-2xl">The archive is empty for this filter.</p>
            <p className="mt-1 text-sm text-[#3A3830]">Be the first to add a pair.</p>
          </div>
        )}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="then-now-grid">
          {pairs.map((p) => (
            <button
              key={p.id}
              data-testid={`then-now-card-${p.id}`}
              onClick={() => setActive(p)}
              className="text-left rounded-2xl border border-black/10 bg-white overflow-hidden group hover:shadow-lg transition-shadow"
            >
              <PairCompareStrip pair={p} height={220} />
              <div className="p-4">
                <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono-tech tracking-[0.24em] uppercase">
                  <span className="text-[color:var(--np-brand,#B85F2A)]">{p.category}</span>
                  <span className="text-[#8E8B7F]">· {p.then_year} → {p.now_year}</span>
                  {p.contributor_source && p.contributor_source !== "naturepulse" && (
                    <span className="rounded-full bg-[#FBF2E1] text-[#241209] border border-black/10 px-2 py-0.5">via {p.contributor_source}</span>
                  )}
                </div>
                <h3 className="mt-1 font-serif-display text-xl leading-tight">{p.title}</h3>
                {p.location && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-[#3A3830]">
                    <MapPin size={11} weight="fill" /> {p.location}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {creating && (
        <SubmitPairModal
          onClose={() => setCreating(false)}
          onCreated={(res) => {
            setCreating(false);
            if (res?.moderation_status === "approved") {
              toast.success("Published to Then & Now");
              load();
            } else {
              toast.success("Submitted for moderation");
            }
          }}
          signedIn={!!user}
        />
      )}

      {active && (
        <DetailModal pair={active} onClose={() => setActive(null)} />
      )}
    </main>
  );
}

/** Slider-driven then/now comparison strip. */
function PairCompareStrip({ pair, height = 260 }) {
  const [pct, setPct] = useState(50);
  return (
    <div
      className="relative w-full overflow-hidden bg-black/5 select-none"
      style={{ height }}
      data-testid={`compare-strip-${pair.id}`}
      onPointerMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const x = Math.max(0, Math.min(r.width, e.clientX - r.left));
        setPct((x / r.width) * 100);
      }}
    >
      <img
        src={pair.now_photo_url} alt={pair.now_caption || "Now"}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div
        className="absolute inset-y-0 left-0 overflow-hidden"
        style={{ width: `${pct}%` }}
      >
        <img
          src={pair.then_photo_url} alt={pair.then_caption || "Then"}
          className="w-full h-full object-cover"
          style={{ minWidth: "100vw", maxWidth: "none", width: "100%" }}
        />
      </div>
      <div
        className="absolute inset-y-0 w-[3px] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
        style={{ left: `calc(${pct}% - 1.5px)` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white p-1.5 shadow">
          <ArrowsHorizontal size={12} weight="bold" className="text-[color:var(--np-brand,#B85F2A)]" />
        </div>
      </div>
      <div className="absolute top-2 left-2 rounded-full bg-black/60 text-white px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
        {pair.then_year}
      </div>
      <div className="absolute top-2 right-2 rounded-full bg-black/60 text-white px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
        {pair.now_year}
      </div>
    </div>
  );
}

function DetailModal({ pair, onClose }) {
  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
         onClick={onClose} data-testid="then-now-detail">
      <div className="bg-[#FBF2E1] w-full max-w-[1000px] max-h-[100vh] md:max-h-[90vh] md:rounded-2xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-black/10">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              {pair.category} · {pair.then_year} → {pair.now_year}
            </div>
            <div className="font-serif-display text-2xl">{pair.title}</div>
          </div>
          <div className="flex items-center gap-2">
            <ShareButton title={pair.title} text={pair.description || ""} variant="chip" testid="then-now-share" />
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-black/5" data-testid="then-now-detail-close">
              <X size={18} weight="bold" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          <PairCompareStrip pair={pair} height={420} />
          <div className="p-5 space-y-3">
            {pair.description && <p className="text-[#241209] leading-relaxed">{pair.description}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-white border border-black/10 p-3">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  Then · {pair.then_year}
                </div>
                <div className="text-sm text-[#241209]">{pair.then_caption || "—"}</div>
              </div>
              <div className="rounded-xl bg-white border border-black/10 p-3">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  Now · {pair.now_year}
                </div>
                <div className="text-sm text-[#241209]">{pair.now_caption || "—"}</div>
              </div>
            </div>
            {pair.location && (
              <div className="text-xs text-[#3A3830] flex items-center gap-1">
                <MapPin size={12} weight="fill" /> {pair.location}
              </div>
            )}
            {pair.contributor_name && (
              <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F]">
                Contributed by {pair.contributor_name} · via {pair.contributor_source}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SubmitPairModal({ onClose, onCreated, signedIn }) {
  const [libOpen, setLibOpen] = useState(null); // 'then' | 'now' | null
  const [form, setForm] = useState({
    title: "", description: "",
    then_photo_url: "", then_year: 2000, then_caption: "",
    now_photo_url: "", now_year: new Date().getFullYear(), now_caption: "",
    location: "", category: "nature", tags: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!signedIn) { toast.error("Sign in to submit a pair"); return; }
    if (form.title.trim().length < 3) { toast.error("Title too short"); return; }
    if (!form.then_photo_url || !form.now_photo_url) { toast.error("Both photos are required"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        then_photo_url: form.then_photo_url,
        then_year: parseInt(form.then_year, 10),
        then_caption: form.then_caption || null,
        now_photo_url: form.now_photo_url,
        now_year: parseInt(form.now_year, 10),
        now_caption: form.now_caption || null,
        location: form.location || null,
        category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const { data } = await http.post("/then-and-now", payload);
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-stretch md:items-center justify-center p-0 md:p-6"
         onClick={onClose} data-testid="then-now-submit-modal">
      <div className="bg-[#FBF2E1] w-full max-w-[900px] max-h-[100vh] md:max-h-[90vh] md:rounded-2xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-black/10 px-5 py-4">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">New pair</div>
            <div className="font-serif-display text-2xl">Submit a Then &amp; Now</div>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-black/5" data-testid="then-now-submit-close">
            <X size={18} weight="bold" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <input
            data-testid="then-now-title"
            value={form.title} onChange={(e) => set("title", e.target.value)}
            placeholder="Title — e.g. Marine Drive, Mumbai · 1970 vs 2026"
            className="w-full rounded-xl border border-black/15 bg-white px-3 py-2.5 text-lg font-serif-display"
          />
          <textarea
            data-testid="then-now-description"
            value={form.description} onChange={(e) => set("description", e.target.value)}
            placeholder="Describe what changed…"
            rows={3}
            className="w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PhotoField label="Then" year={form.then_year} onYear={(v) => set("then_year", v)}
                        url={form.then_photo_url} onUrl={(v) => set("then_photo_url", v)}
                        caption={form.then_caption} onCaption={(v) => set("then_caption", v)}
                        onOpenLib={() => setLibOpen("then")}
                        testidPrefix="then-now-then" />
            <PhotoField label="Now" year={form.now_year} onYear={(v) => set("now_year", v)}
                        url={form.now_photo_url} onUrl={(v) => set("now_photo_url", v)}
                        caption={form.now_caption} onCaption={(v) => set("now_caption", v)}
                        onOpenLib={() => setLibOpen("now")}
                        testidPrefix="then-now-now" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              data-testid="then-now-location"
              value={form.location} onChange={(e) => set("location", e.target.value)}
              placeholder="Location (city, state, country)"
              className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm md:col-span-2"
            />
            <select
              data-testid="then-now-category-select"
              value={form.category} onChange={(e) => set("category", e.target.value)}
              className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"
            >
              {CATEGORIES.filter((c) => c.key).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <input
            data-testid="then-now-tags"
            value={form.tags} onChange={(e) => set("tags", e.target.value)}
            placeholder="tags (comma-separated) — e.g. mangrove, kochi, monsoon"
            className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"
          />
        </div>
        <footer className="border-t border-black/10 p-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Cancel</button>
          <button
            data-testid="then-now-submit-confirm"
            onClick={submit}
            disabled={busy}
            className="rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-60"
          >
            {busy ? "Submitting…" : "Submit for review"}
          </button>
        </footer>
      </div>
      <PhotoLibraryPicker
        open={!!libOpen}
        onClose={() => setLibOpen(null)}
        onSelect={({ url }) => {
          if (libOpen === "then") set("then_photo_url", url);
          if (libOpen === "now") set("now_photo_url", url);
          setLibOpen(null);
        }}
      />
    </div>
  );
}

function PhotoField({ label, year, onYear, url, onUrl, caption, onCaption, onOpenLib, testidPrefix }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{label} photo</div>
        <input
          data-testid={`${testidPrefix}-year`}
          type="number"
          value={year} onChange={(e) => onYear(e.target.value)}
          className="w-20 rounded-lg border border-black/15 bg-white px-2 py-1 text-sm text-right"
        />
      </div>
      {url ? (
        <div className="relative aspect-[4/3] rounded-lg bg-black/5 overflow-hidden">
          <img src={url} alt={label} className="w-full h-full object-cover" />
          <button
            data-testid={`${testidPrefix}-change`}
            onClick={onOpenLib}
            className="absolute bottom-2 right-2 rounded-full bg-black/70 text-white px-2 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase"
          >Change</button>
        </div>
      ) : (
        <button
          data-testid={`${testidPrefix}-pick`}
          onClick={onOpenLib}
          className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-black/20 flex items-center justify-center text-[#8E8B7F] hover:bg-black/5"
        >
          <div className="text-center">
            <Camera size={20} weight="fill" className="mx-auto mb-1" />
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase">Pick from Photo Library</div>
          </div>
        </button>
      )}
      <input
        data-testid={`${testidPrefix}-url`}
        value={url} onChange={(e) => onUrl(e.target.value)}
        placeholder="…or paste a URL"
        className="w-full rounded-lg border border-black/15 bg-white px-2 py-1.5 text-xs"
      />
      <input
        data-testid={`${testidPrefix}-caption`}
        value={caption} onChange={(e) => onCaption(e.target.value)}
        placeholder="Caption"
        className="w-full rounded-lg border border-black/15 bg-white px-2 py-1.5 text-sm"
      />
    </div>
  );
}
